import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Calendar, TrendingUp, TrendingDown } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const axCfg = { headers: { "X-API-Key": process.env.REACT_APP_API_KEY || "" } };

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 24 }, (_, h) => h);

function fmt$(v) {
  const n = Number(v || 0);
  return (n >= 0 ? "+$" : "-$") + Math.abs(n).toLocaleString("en", { maximumFractionDigits: 0 });
}

function cellColor(pnl, maxAbs) {
  if (!pnl || maxAbs === 0) return "rgba(255,255,255,0.04)";
  const intensity = Math.min(1, Math.abs(pnl) / maxAbs);
  const alpha = 0.15 + intensity * 0.65;
  return pnl > 0
    ? `rgba(74,222,128,${alpha.toFixed(2)})`
    : `rgba(255,93,108,${alpha.toFixed(2)})`;
}

export default function PnlHeatmap() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(60);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/analytics/pnl-heatmap?days=${days}`, axCfg)
      .then(r => { if (mounted) setD(r.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [days]);

  const maxAbsPnl = useMemo(() => {
    if (!d?.grid) return 1;
    let m = 0;
    for (const row of d.grid) for (const c of row) if (Math.abs(c.pnl) > m) m = Math.abs(c.pnl);
    return m || 1;
  }, [d]);

  if (!d) {
    return (
      <section style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "14px 16px", color: "rgba(255,255,255,0.4)" }}>
        Loading PnL heatmap…
      </section>
    );
  }

  return (
    <section style={{
      background: "rgba(255,255,255,0.025)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: 14,
      padding: "14px 16px",
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Calendar style={{ width: 14, height: 14, color: "#94a3b8" }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: "rgba(255,255,255,0.7)", textTransform: "uppercase" }}>
            PnL Heatmap
          </span>
          <span style={{ fontSize: 9, color: "rgba(255,255,255,0.35)" }}>
            {d.total_trades} trades · UTC
          </span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {[14, 30, 60, 90].map(n => (
            <button key={n} onClick={() => setDays(n)} style={{
              padding: "3px 9px", borderRadius: 6, fontSize: 9, fontWeight: 700,
              background: days === n ? "rgba(74,222,128,0.15)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${days === n ? "rgba(74,222,128,0.4)" : "rgba(255,255,255,0.08)"}`,
              color: days === n ? "#4ade80" : "rgba(255,255,255,0.5)",
              cursor: "pointer",
            }}>
              {n}d
            </button>
          ))}
        </div>
      </div>

      {/* Heatmap grid */}
      <div style={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
        {/* Day labels */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 16 }}>
          {DOW.map((day, i) => (
            <div key={day} style={{
              fontSize: 8, fontWeight: 600, color: "rgba(255,255,255,0.4)",
              width: 22, height: 16, display: "flex", alignItems: "center",
            }}>
              {day}
            </div>
          ))}
        </div>

        {/* Grid */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          {/* Hour labels */}
          <div style={{ display: "flex", gap: 2, marginBottom: 2 }}>
            {HOURS.map(h => (
              <div key={h} style={{
                flex: 1, minWidth: 16, height: 14, textAlign: "center",
                fontSize: 7, color: "rgba(255,255,255,0.3)",
              }}>
                {h % 3 === 0 ? h : ""}
              </div>
            ))}
          </div>
          {/* Day rows */}
          {DOW.map((_, dow) => (
            <div key={dow} style={{ display: "flex", gap: 2, marginBottom: 2 }}>
              {HOURS.map(h => {
                const cell = d.grid?.[dow]?.[h] || { pnl: 0, n: 0, wins: 0 };
                const isHover = hover && hover.dow === dow && hover.hour === h;
                return (
                  <div key={h}
                    onMouseEnter={() => setHover({ dow, hour: h, ...cell })}
                    onMouseLeave={() => setHover(null)}
                    style={{
                      flex: 1, minWidth: 16, height: 16, borderRadius: 3,
                      background: cellColor(cell.pnl, maxAbsPnl),
                      border: cell.n > 0 ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(255,255,255,0.03)",
                      cursor: cell.n > 0 ? "pointer" : "default",
                      transition: "transform 0.1s",
                      transform: isHover ? "scale(1.4)" : "scale(1)",
                      zIndex: isHover ? 10 : 1,
                      position: "relative",
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Hover tooltip */}
      {hover && hover.n > 0 && (
        <div style={{
          marginTop: 10, padding: "8px 12px", borderRadius: 8,
          background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.08)",
          fontSize: 10, fontFamily: "monospace", color: "rgba(255,255,255,0.85)",
          display: "flex", justifyContent: "space-between",
        }}>
          <span>{DOW[hover.dow]} · {String(hover.hour).padStart(2,"0")}:00 UTC</span>
          <span style={{ color: hover.pnl >= 0 ? "#4ade80" : "#ff5d6c", fontWeight: 700 }}>
            {fmt$(hover.pnl)}
          </span>
          <span style={{ color: "rgba(255,255,255,0.5)" }}>
            {hover.n} trades · {hover.wr ?? Math.round(hover.wins/hover.n*100)}% WR
          </span>
        </div>
      )}

      {/* Best / worst rollups */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
        <div style={{ background: "rgba(74,222,128,0.05)", border: "1px solid rgba(74,222,128,0.15)", borderRadius: 10, padding: "8px 10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 9, fontWeight: 700, color: "#4ade80", letterSpacing: "0.14em", marginBottom: 6 }}>
            <TrendingUp style={{ width: 10, height: 10 }} />
            BEST WINDOWS
          </div>
          {(d.best_cells || []).slice(0, 3).map((c, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 2 }}>
              <span style={{ color: "rgba(255,255,255,0.55)" }}>{DOW[c.dow]} {String(c.hour).padStart(2,"0")}:00</span>
              <span style={{ color: "#4ade80", fontFamily: "monospace", fontWeight: 700 }}>{fmt$(c.pnl)}</span>
              <span style={{ color: "rgba(255,255,255,0.35)" }}>{c.n}t · {c.wr}%</span>
            </div>
          ))}
        </div>
        <div style={{ background: "rgba(255,93,108,0.05)", border: "1px solid rgba(255,93,108,0.15)", borderRadius: 10, padding: "8px 10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 9, fontWeight: 700, color: "#ff5d6c", letterSpacing: "0.14em", marginBottom: 6 }}>
            <TrendingDown style={{ width: 10, height: 10 }} />
            WORST WINDOWS
          </div>
          {(d.worst_cells || []).slice(0, 3).map((c, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 2 }}>
              <span style={{ color: "rgba(255,255,255,0.55)" }}>{DOW[c.dow]} {String(c.hour).padStart(2,"0")}:00</span>
              <span style={{ color: "#ff5d6c", fontFamily: "monospace", fontWeight: 700 }}>{fmt$(c.pnl)}</span>
              <span style={{ color: "rgba(255,255,255,0.35)" }}>{c.n}t · {c.wr}%</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
